import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cardLgSx, primaryButtonSx } from './pvtaStyles';
import { extractApiDetail, useConfirmEmailChange } from '../hooks/emailChange';

type ConfirmState = 'loading' | 'success' | 'error';

const EmailChangeConfirm: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { confirmEmailChange } = useConfirmEmailChange();
    const [state, setState] = useState<ConfirmState>('loading');
    const [message, setMessage] = useState('');
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) {
            return;
        }
        hasRun.current = true;
        const token = searchParams.get('token') ?? '';
        if (!token) {
            setState('error');
            setMessage('Kein Bestätigungstoken gefunden. Bitte nutze den Link aus der E-Mail.');
            return;
        }
        confirmEmailChange(token)
            .then((result) => {
                setState('success');
                setMessage(result.detail || 'Deine E-Mail-Adresse wurde aktualisiert.');
            })
            .catch((error: unknown) => {
                setState('error');
                setMessage(
                    extractApiDetail(error) ?? 'Der Bestätigungslink ist ungültig oder abgelaufen.'
                );
            });
    }, [confirmEmailChange, searchParams]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                background: 'linear-gradient(160deg, var(--pvta-bg) 0%, var(--pvta-green-100) 100%)',
            }}
        >
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', px: { xs: 0, sm: 2 } }}>
                <Paper
                    elevation={0}
                    sx={{ ...cardLgSx, p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 460, boxSizing: 'border-box', textAlign: 'center' }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Georgia, "Times New Roman", serif',
                            fontWeight: 700,
                            fontSize: 26,
                            color: 'var(--pvta-green-900)',
                            mb: 2,
                        }}
                    >
                        E-Mail-Adresse bestätigen
                    </Typography>

                    {state === 'loading' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
                            <CircularProgress sx={{ color: 'var(--pvta-green-800)' }} />
                            <Typography variant="body2" sx={{ color: 'var(--pvta-muted)' }}>
                                Bestätigung wird geprüft …
                            </Typography>
                        </Box>
                    )}

                    {state === 'success' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 56, color: 'var(--pvta-success-text)' }} />
                            <Typography variant="body1">{message}</Typography>
                        </Box>
                    )}

                    {state === 'error' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                            <ErrorOutlineRoundedIcon sx={{ fontSize: 56, color: 'var(--pvta-danger-text)' }} />
                            <Typography variant="body1">{message}</Typography>
                        </Box>
                    )}

                    {state !== 'loading' && (
                        <Button sx={{ ...primaryButtonSx, mt: 3 }} fullWidth onClick={() => navigate('/')}>
                            Zur Anmeldung
                        </Button>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default EmailChangeConfirm;
